import { Component } from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import { AlertController, NavController, NavParams } from "ionic-angular";
import moment from "moment";
import { mapVolunteer } from "../../../mappers/resource-management/volunteer-mapper";
import { BusinessPhoneNumberType } from "../../../models/contact/business-phone-number";
import CollapsibleEvent from "../../../models/events/collapsible-modal.event";
import LookupValue from "../../../models/patient-chart/lookupValue";
import {
  isProfessionalProvider,
  isVolunteer,
} from "../../../models/resource-management/company-resource-type-guards";
import Credential from "../../../models/resource-management/credential";
import CredentialedResource from "../../../models/resource-management/credentialed-resource";
import ProfessionalProvider from "../../../models/resource-management/professional-provider";
import { RadiusMeasurement } from "../../../models/resource-management/volunteers/radius-measurement";
import Volunteer from "../../../models/resource-management/volunteers/volunteer";
import VolunteerLocationPreference from "../../../models/resource-management/volunteers/volunteer-location-preference";
import VolunteerMatchTerm from "../../../models/resource-management/volunteers/volunteer-match-term";
import { commonVar } from "../../../providers/common-var/common";
import { ResourceManagementService } from "../../../providers/resource-management/resource-management.service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import PhoneUtils from "../../../utils/phone.utils";

@Component({
  selector: "company-resources-edit",
  templateUrl: "company-resources-edit.html",
})
export class CompanyResourcesEditPage {
  providerForm: FormGroup;
  stateList = commonVar.STATE_LIST;
  phoneMask = commonVar.PHONE_US_MASK;
  phonePlaceHolder = commonVar.PHONE_US_PLACEHOLDER;
  emailRegex = commonVar.EMAIL_REGEX;
  fromDateStartMin: any;
  fromDateStartMax: any;
  toDateStartMin: any;
  toDateStartMax: any;
  datePlacementPreference: PlacementArray = ["bottom-left"];
  provider: CredentialedResource;
  isProvider: boolean = false;
  isVolunteer: boolean = false;
  showGeneralDetails: boolean = true;
  showProfessionalDetails: boolean = true;
  showVolunteerSchedule: boolean = true;
  showVolunteerSkills: boolean = true;
  showCredentials: boolean = true;
  private _hasNonProviderCliniciansEnabled: boolean = false;
  private _hasProviderEnabled: boolean = false;
  private _hasVolunteersEnabled: boolean = false;
  languages: LookupValue[];
  credentialTypes: LookupValue[];
  volunteerTypes: LookupValue[];
  volunteerActivityCategories: VolunteerMatchTerm[];
  public days: string[] = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
  ];

  constructor(
    private fb: FormBuilder,
    public navCtrl: NavController,
    private navParams: NavParams,
    private alertController: AlertController,
    private stm: StateManagementService,
    private resourceManagementService: ResourceManagementService
  ) {
    if (this.navParams.get("provider")) {
      this.provider = this.navParams.get("provider");
    } else {
      let contactType = this.navParams.get("contactType");
      if (!contactType) {
        this.provider = new ProfessionalProvider();
        this.provider.resourceType = "1";
      } else if (parseInt(contactType) === 1) {
        this.provider = new ProfessionalProvider();
        this.provider.resourceType = "1";
      } else if (parseInt(contactType) === 4) {
        this.provider = new CredentialedResource();
        this.provider.resourceType = "4";
      } else if (parseInt(contactType) === 5) {
        this.provider = new Volunteer();
        this.provider.resourceType = "5";
      }
    }

    if (this.navParams.get("hasNonProviderCliniciansEnabled")) {
      this.hasNonProviderCliniciansEnabled = this.navParams.get(
        "hasNonProviderCliniciansEnabled"
      );
    }

    if (this.navParams.get("hasProviderEnabled")) {
      this.hasProviderEnabled = this.navParams.get("hasProviderEnabled");
    }

    if (this.navParams.get("hasVolunteerEnabled")) {
      this.hasVolunteersEnabled = this.navParams.get("hasVolunteerEnabled");
    }

    if (isProfessionalProvider(this.provider)) {
      this.isProvider = true;
    } else if (isVolunteer(this.provider)) {
      this.isVolunteer = true;
      this.languages = this.navParams.get("languages");
    }
  }

  /**
   * View entering event handler
   */
  ionViewWillEnter() {
    this.credentialTypes = this.credentialTypes.filter((x) => {
      return x.id !== 27; // OTHER
    });

    this.credentialTypes.push(new LookupValue(27, "Other"));

    this.fromDateStartMin = null; // {year: 2010, month: 1, day: 1,};
    this.fromDateStartMax = {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      day: new Date().getDate(),
    };

    this.toDateStartMax = null;
  }

  /**
   * View loading event handler
   */
  ionViewWillLoad() {
    this.credentialTypes = this.navParams.get("credentialTypes");
    this.volunteerTypes = this.navParams.get("volunteerTypes");
    this.volunteerActivityCategories = this.navParams.get(
      "volunteerActivityCategories"
    );

    // put other on the end
    let volunteerMatchTerms = this.volunteerActivityCategories.filter(
      (x) => x.term === "Other"
    );
    if (volunteerMatchTerms && volunteerMatchTerms.length === 0) {
      this.volunteerActivityCategories.push({
        term: "Other",
        id: 0,
      });
    }

    let officeNumber = this.provider.findPhoneNumberByType(3);
    let faxNumber = this.provider.findPhoneNumberByType(2);
    let address = this.provider.findAddressByType(1);

    this.providerForm = new FormGroup({
      // General Details
      resourceType: this.fb.control(this.provider.resourceType),
      resourceStatus: this.fb.control(this.provider.resourceStatus),
      firstName: new FormControl(this.provider.firstName, [
        Validators.required,
      ]),
      middleInitial: new FormControl(this.provider.middleInitial),
      lastName: new FormControl(this.provider.lastName, [Validators.required]),
      preferredName: this.fb.control(this.provider.preferredName),
      emailAddress: this.fb.control(
        this.provider.emailAddress,
        Validators.compose([Validators.pattern(this.emailRegex)])
      ),
      // phone array (fax and office)
      phoneNumbers: this.fb.array([
        this.fb.group({
          id: this.fb.control(officeNumber.id),
          phoneNumber: this.fb.control(
            PhoneUtils.applyPhoneMask(officeNumber.phoneNumber),
            [
              Validators.required,
              Validators.pattern("^\\([0-9]{3}\\)[0-9]{3}-[0-9]{4}$"),
            ]
          ),
          phoneNumberType: BusinessPhoneNumberType.WORK,
        }),
        this.fb.group({
          id: this.fb.control(faxNumber.id),
          phoneNumber: this.fb.control(
            PhoneUtils.applyPhoneMask(faxNumber.phoneNumber),
            [Validators.pattern("^\\([0-9]{3}\\)[0-9]{3}-[0-9]{4}$")]
          ),
          phoneNumberType: BusinessPhoneNumberType.FAX,
        }),
      ]),
      // office address
      addresses: this.fb.array([
        new FormGroup({
          id: this.fb.control(address.id),
          address1: new FormControl(address.address1, [Validators.required]),
          address2: new FormControl(address.address2),
          city: new FormControl(address.city, [Validators.required]),
          state: new FormControl(address.state, [Validators.required]),
          zip: new FormControl(
            address.zip,
            Validators.compose([
              Validators.pattern("^[0-9]{5}$"),
              Validators.required,
            ])
          ),
        }),
      ]),
      // Credential/Professional Details
      // credentials array -- holds 1 to start
      credentials: this.fb.array(
        [],
        this.duplicateCredentialValidation.bind(this)
      ),
    });

    if (this.provider.credentials && this.provider.credentials.length > 0) {
      this.provider.credentials.forEach((x) => {
        this.initializeCredentialElements(x);
      });
    }

    if (isProfessionalProvider(this.provider)) {
      this.initProfessionalProvider();
    } else if (isVolunteer(this.provider)) {
      this.initVolunteer();
    }

    // toggling between provider and non-provider clinician adjusts the fields
    this.providerForm.get("resourceType").valueChanges.subscribe((x) => {
      if (isNaN(x)) return;
      let val = parseInt(x);

      if (val === 1) {
        this.initProfessionalProvider();
        this.isProvider = true;
      } else if (val === 4) {
        this.removeProfessionalProvider();
        this.isProvider = false;
      }
    });
  }

  /**
   * Inits the volunteer form
   * @private
   */
  private initVolunteer(): void {
    let v = mapVolunteer(this.provider);

    console.log(v);
    this.providerForm.addControl("dateOfBirth", this.fb.control(v.dateOfBirth));
    this.providerForm.addControl("notes", this.fb.control(v.notes));
    this.providerForm.addControl("gender", this.fb.control(v.gender));
    this.providerForm.addControl("languages", this.fb.control(v.languages));
    this.providerForm.addControl(
      "volunteerType",
      this.fb.control(v.convertVolunteerTypeToArray(), [Validators.required])
    );
    this.providerForm.addControl("title", this.fb.control(v.title));
    this.providerForm.addControl(
      "trainingCompletionDate",
      this.fb.control(v.trainingCompletionDate)
    );

    let a = new FormArray([]);
    let b = new FormArray([]);

    this.providerForm.addControl(
      "preferences",
      this.fb.group({
        parentResourceType: this.fb.control("volunteer"),
        schedule: this.fb.group({
          locationPreferences: this.fb.array([]),
          availabilities: a,
        }),
        skillsAndInterests: b,
      })
    );

    for (let i = 0; i < this.days.length; i++) {
      let d = v.preferences.schedule.findAvailability(this.days[i]);
      let fg = this.fb.group({
        day: this.fb.control(this.days[i]),
        selected: this.fb.control(d.selected),
        morning: this.fb.control(d.morning),
        afternoon: this.fb.control(d.afternoon),
        evening: this.fb.control(d.evening),
      });
      a.push(fg);
    }

    this.volunteerActivityCategories.forEach((x: VolunteerMatchTerm) => {
      let l = v.preferences.findSkillOrInterestById(x.term);
      let fg = this.fb.group({
        term: l.term,
        selected: l.selected,
        other: l.other,
      });

      // handle changes
      fg.valueChanges.subscribe((x: any) =>
        this.handleSkillsAndInterestsChange(x)
      );

      b.push(fg);
    });

    v.preferences.schedule.locationPreferences.forEach(
      (x: VolunteerLocationPreference) => {
        this.initLocationPreferences(x);
      }
    );
  }

  handleSkillsAndInterestsChange(x: any) {
    if (x.term === "Other") {
      if (!x.selected) {
        this.otherSkill.patchValue("", { emitEvent: false });
      }
    }
  }

  /**
   * Inits the professional provider details
   * @private
   */
  private initProfessionalProvider(): void {
    this.providerForm.addControl(
      "groupAffiliation",
      this.fb.control((this.provider as ProfessionalProvider).groupAffiliation)
    );
    this.providerForm.addControl(
      "dea",
      new FormControl(
        (this.provider as ProfessionalProvider).dea,
        Validators.pattern("^[a-zA-Z]{2}[0-9]{7}$")
      )
    );
    this.providerForm.addControl(
      "npi",
      new FormControl(
        (this.provider as ProfessionalProvider).npi,
        [Validators.required, Validators.pattern("^[0-9]{10}$")],
        [this.validateProviderAvailable.bind(this)]
      )
    );
    this.providerForm.addControl(
      "taxonomyCode",
      this.fb.control((this.provider as ProfessionalProvider).taxonomyCode)
    );
    this.providerForm.addControl(
      "taxId",
      this.fb.control(
        (this.provider as ProfessionalProvider).taxId,
        Validators.pattern("^[0-9]+$")
      )
    );
  }

  /**
   * Removes the professional provider details
   * @private
   */
  private removeProfessionalProvider(): void {
    this.providerForm.removeControl("groupAffiliation");
    this.providerForm.removeControl("dea");
    this.providerForm.removeControl("npi");
    this.providerForm.removeControl("taxonomyCode");
    this.providerForm.removeControl("taxId");
  }

  /**
   * Init the date ranges
   */
  private initDateRanges(control: FormGroup) {
    let endDateMin = moment(
      moment(control.get("validFromDatetime").value).format("MM/DD/YYYY"),
      "MM/DD/YYYY"
    );
    this.toDateStartMin = this.getDateFromForm(endDateMin, 1);
    this.fromDateStartMax = this.getDateFromForm(endDateMin, 0);
  }

  getDateFromForm(date, days) {
    return {
      day: parseInt(date.format("DD")) + days,
      month: parseInt(date.format("MM")),
      year: parseInt(date.format("YYYY")),
    };
  }

  duplicateCredentialValidation(a: FormArray) {
    let countHash = {};
    let hasDuplicated: boolean = false;
    a.controls
      .filter((x) => x.get("type").value !== 27)
      .forEach((x: FormGroup) => {
        if (
          x.get("type").value &&
          x.get("licenseId").value &&
          x.get("issueState").value
        ) {
          let key = `key_${x.get("type").value}_${x.get("licenseId").value}_${
            x.get("issueState").value
          }`;
          if (!countHash[key]) {
            countHash[key] = 1;
          } else {
            hasDuplicated = true;
          }
        }
      });

    return !hasDuplicated ? null : { duplicatedCredential: true };
  }

  validateProviderAvailable(control: AbstractControl) {
    return this.resourceManagementService
      .checkDuplicateProvider(control.value)
      .map((res) => {
        let p = this.provider as ProfessionalProvider;
        if (control.value === p.npi) {
          return null;
        }
        return !res ? null : { npiTaken: true };
      });
  }

  private togglePhone(on: boolean): void {
    if (!on) {
      this.phoneNumbers.controls.forEach((x, y) => {
        x.get("phoneNumber").clearValidators();
        x.get("phoneNumber").setValue(
          PhoneUtils.removePhoneMask(x.get("phoneNumber").value)
        );
      });
    } else {
      this.phoneNumbers.controls.forEach((x, y) => {
        x.get("phoneNumber").setValidators([
          Validators.required,
          Validators.pattern("^\\([0-9]{3}\\)[0-9]{3}-[0-9]{4}$"),
        ]);
        x.get("phoneNumber").setValue(
          PhoneUtils.applyPhoneMask(x.get("phoneNumber").value)
        );
      });
    }
  }

  // region Event Handlers

  /**
   * Handles the form submission
   */
  submitForm() {
    this.togglePhone(false);

    if (this.isVolunteer) {
      let dob = this.providerForm.get("dateOfBirth").value;
      let trainingCompletionDate = this.providerForm.get(
        "trainingCompletionDate"
      ).value;
      let vType = this.providerForm.get("volunteerType").value;
      let ls = this.providerForm.get("languages").value;

      if (vType) {
        let j = 0;
        for (let i = 0; i < vType.length; i++) {
          j += parseInt(vType[i]);
        }
        this.providerForm.get("volunteerType").setValue(j);
      }

      if (ls) {
        this.providerForm.get("languages").setValue(ls.map(Number));
      }

      if (dob) {
        this.providerForm.get("dateOfBirth").setValue(moment.utc(dob).format());
      }

      if (trainingCompletionDate) {
        this.providerForm
          .get("trainingCompletionDate")
          .setValue(moment.utc(trainingCompletionDate).format());
      }
    }

    this.credentials.controls.forEach((x, y) => {
      let validFromDate = x.get("validFromDatetime").value;
      let validToDate = x.get("validToDatetime").value;

      // prepare dates for send back
      if (validFromDate) {
        x.get("validFromDatetime").setValue(moment.utc(validFromDate).format());
      }
      if (validToDate) {
        x.get("validToDatetime").setValue(moment.utc(validToDate).format());
      }
    });

    if (this.provider.id > 0) {
      this.resourceManagementService
        .updateProfessionalProvider(this.provider.id, this.providerForm.value)
        .pipe()
        .take(1)
        .subscribe(
          (x) => {
            this.navCtrl.pop();
          },
          (error) => {
            this.togglePhone(true);
          }
        );
    } else {
      this.resourceManagementService
        .createProfessionalProvider(this.providerForm.value)
        .pipe()
        .take(1)
        .subscribe(
          (x) => {
            this.navCtrl.pop();
          },
          (error) => {
            this.togglePhone(true);
          }
        );
    }
  }

  /**
   * Handles the cancel event
   */
  cancel() {
    let alert = this.alertController.create({
      title: "Cancel Confirmation",
      message: "Are you sure you wish to cancel",
      buttons: [
        {
          text: "No",
          handler: () => {},
        },
        {
          text: "Yes",
          handler: () => {
            this.navCtrl.pop();
          },
        },
      ],
    });
    alert.present().then((r) => console.log("Handled"));
  }

  /**
   * Handles the click for the section
   * @param $e
   */
  onCollapseClicked($e: CollapsibleEvent): void {
    if ($e.controlId === "general-details") {
      this.showGeneralDetails = $e.isOpen;
    } else if ($e.controlId === "professional-details") {
      this.showProfessionalDetails = $e.isOpen;
    } else if ($e.controlId === "credentials") {
      this.showCredentials = $e.isOpen;
    } else if ($e.controlId === "schedule") {
      this.showVolunteerSchedule = $e.isOpen;
    } else if ($e.controlId === "skills") {
      this.showVolunteerSkills = $e.isOpen;
    }
  }

  /**
   * Remove a credential from the list
   * @param index the item in the list to remove
   * @param item the control to remove
   */
  deleteCredential(index: number, item: AbstractControl): void {
    let alert = this.alertController.create({
      title: "Confirm delete credential",
      message:
        "Are you sure you want to permanently delete this credential? Doing so will not make it usable in other parts of the system.  Perhaps you mean to use Operating As?",
      buttons: [
        {
          text: "No",
          handler: () => {},
        },
        {
          text: "Yes",
          handler: () => {
            // hide from view and clear validators if this credential is delete from the api
            if (item.get("id").value > 0) {
              item.get("deleted").setValue(true);
              item.get("type").clearValidators();
              item.get("licenseId").clearValidators();
              item.get("issueState").clearValidators();
              item.get("validFromDatetime").clearValidators();
            } else {
              // credential has never been saved so just purge
              this.credentials.removeAt(index);
            }
          },
        },
      ],
    });
    alert.present().then((r) => console.log("Handled"));
  }

  /**
   * Adds a new credential to the formarray
   */
  addCredential(): void {
    this.initializeCredentialElements(new Credential());
  }

  /**
   * Creates a new credential in the form array from a credential
   * @param credential the credential to add
   */
  initializeCredentialElements(credential: Credential): void {
    let initialDate;

    if (credential.validFromDatetime) {
      initialDate = credential.validFromDatetime;
    } else {
      // convert NOW in UTC to NOW - the client's offset
      initialDate = moment
        .utc()
        .zone(new Date().getTimezoneOffset())
        .format("MM/DD/YYYY");
    }

    let formGroup = this.fb.group({
      id: this.fb.control(credential.id),
      deleted: this.fb.control(credential.deleted),
      type: this.fb.control(credential.type, [Validators.required]),
      other: this.fb.control(credential.other),
      licenseId: this.fb.control(credential.licenseId, [Validators.required]),
      issueState: this.fb.control(credential.issueState, [Validators.required]),
      validFromDatetime: this.fb.control(initialDate, [Validators.required]),
      validToDatetime: this.fb.control(credential.validToDatetime),
      operatingAs: this.fb.control(credential.operatingAs),
    });

    this.credentials.push(formGroup);
    this.initDateRanges(formGroup);

    // handles the date change from date start
    formGroup.get("validFromDatetime").valueChanges.subscribe((x) => {
      let endDateMin = moment(moment(x).format("MM/DD/YYYY"), "MM/DD/YYYY");
      this.toDateStartMin = this.getDateFromForm(endDateMin, 1);
      this.fromDateStartMax = this.getDateFromForm(endDateMin, 0);
    });

    // handles changes from credential type
    formGroup.get("type").valueChanges.subscribe((x) => {
      if (x !== 27) {
        formGroup.get("other").setValue("");
      }
    });
  }

  initLocationPreferences(pref: VolunteerLocationPreference): void {
    let formGroup = this.fb.group({
      radius: this.fb.control(pref.radius),
      zipCode: this.fb.control(pref.zipCode),
      radiusMeasurement: this.fb.control(
        pref.radiusMeasurement ? pref.radiusMeasurement : RadiusMeasurement.MILE
      ),
    });

    this.locationPreferences.push(formGroup);
  }

  addLocationPreference() {
    let pref = {
      radius: 0,
      zipCode: "",
      radiusMeasurement: RadiusMeasurement.MILE,
    };
    this.initLocationPreferences(pref);
  }

  // endregion

  // region Properties
  get isEmployee() {
    return this.provider.isEmployee;
  }

  get firstName() {
    return this.providerForm.get("firstName");
  }

  get emailAddress() {
    return this.providerForm.get("emailAddress");
  }

  get lastName() {
    return this.providerForm.get("lastName");
  }

  get npi() {
    return this.providerForm.get("npi");
  }

  get zip(): FormControl {
    return this.providerForm
      .get("addresses")
      .get("0")
      .get("zip") as FormControl;
  }

  get address1(): FormControl {
    return this.providerForm
      .get("addresses")
      .get("0")
      .get("address1") as FormControl;
  }

  get city(): FormControl {
    return this.providerForm
      .get("addresses")
      .get("0")
      .get("city") as FormControl;
  }

  get state(): FormControl {
    return this.providerForm
      .get("addresses")
      .get("0")
      .get("state") as FormControl;
  }

  get dea() {
    return this.providerForm.get("dea");
  }

  get taxId() {
    return this.providerForm.get("taxId");
  }

  get trainingCompletionDate() {
    return this.providerForm.get("trainingCompletionDate");
  }

  get volunteerType() {
    return this.providerForm.get("volunteerType");
  }

  get phoneNumbers(): FormArray {
    return this.providerForm.get("phoneNumbers") as FormArray;
  }

  get addresses(): FormArray {
    return this.providerForm.get("addresses") as FormArray;
  }

  get locationPreferences(): FormArray {
    return this.providerForm
      .get("preferences")
      .get("schedule")
      .get("locationPreferences") as FormArray;
  }

  get availabilities(): FormArray {
    return this.providerForm
      .get("preferences")
      .get("schedule")
      .get("availabilities") as FormArray;
  }

  get credentials(): FormArray {
    return this.providerForm.get("credentials") as FormArray;
  }

  get skillsAndInterests(): FormArray {
    return this.providerForm
      .get("preferences")
      .get("skillsAndInterests") as FormArray;
  }

  get otherSkill(): FormControl {
    let i = this.skillsAndInterests.length;
    return this.skillsAndInterests.controls[i - 1].get("other") as FormControl;
  }

  get primaryPhoneNumber(): FormControl {
    return this.providerForm
      .get("phoneNumbers")
      .get("0")
      .get("phoneNumber") as FormControl;
  }

  get faxNumber(): FormControl {
    return this.providerForm
      .get("phoneNumbers")
      .get("1")
      .get("phoneNumber") as FormControl;
  }

  get providerNpi(): string {
    let p = this.provider as ProfessionalProvider;
    return p.npi;
  }

  get hasNonProviderCliniciansEnabled(): boolean {
    return this._hasNonProviderCliniciansEnabled;
  }

  set hasNonProviderCliniciansEnabled(value: boolean) {
    this._hasNonProviderCliniciansEnabled = value;
  }

  get hasProviderEnabled(): boolean {
    return this._hasProviderEnabled;
  }

  set hasProviderEnabled(value: boolean) {
    this._hasProviderEnabled = value;
  }

  get attachedToPatients(): boolean {
    if (isProfessionalProvider(this.provider)) {
      return (this.provider as ProfessionalProvider).attachedToPatients;
    }

    return false;
  }

  get hasVolunteersEnabled(): boolean {
    return this._hasVolunteersEnabled;
  }

  set hasVolunteersEnabled(value: boolean) {
    this._hasVolunteersEnabled = value;
  }

  // endregion
}



// WEBPACK FOOTER //
// ./src/pages/company-resources/company-resources-edit/company-resources-edit.ts